import React, { useEffect, useState } from 'react';
import { getApiBaseUrl, getProductGalleryPaths } from "../utils/apiAssetUrl";
import '../CSS/Global.css';
import { useNavigate, useParams } from 'react-router-dom';
import productadd from "../Images/productadd.png"
import { useToast } from '@chakra-ui/react';
import { validateImageFileForUpload, IMAGE_ACCEPT_ATTR, IMAGE_TYPES_HELPER_TEXT } from "../utils/imageUploadRules";
import LocationProductDetailsForm from "./LocationProductDetailsForm";
import { resolveIsLocationCategory } from "../utils/locationCategory";
import {
    getDefaultLocationDetails,
    getListingTokenAmountFromStore,
    normalizeLocationDetailsFromApi,
    validateLocationDetailsClient,
} from "../utils/locationProductDetails";

const MAX_PRODUCT_PHOTOS = 10;

function appendLocationDocsToFormData(formData, docFiles) {
    const map = {
        noc: "location_doc_noc",
        business_license: "location_doc_business_license",
        fire_safety: "location_doc_fire_safety",
        local_authority: "location_doc_local_authority",
        insurance: "location_doc_insurance",
    };
    Object.entries(map).forEach(([k, field]) => {
        const f = docFiles[k];
        if (f instanceof File) formData.append(field, f);
    });
}

const EditProduct = () => {
    const [productName, setProductName] = useState('');
    const [mrp, setMrp] = useState('');
    const [sellingPrice, setSellingPrice] = useState('');
    const [shortDescription, setShortDescription] = useState('');
    const [existingPaths, setExistingPaths] = useState([]);
    const [newFiles, setNewFiles] = useState([]);
    const login = JSON.parse(localStorage.getItem("login")) || "";
    let navigate = useNavigate();
    const { id } = useParams();
    const [shiftHour, setShiftHour] = useState('');
    const [selectedUnit, setSelectedUnit] = useState('');
    const [quantity, setSelectedQuantity] = useState('');
    const [units, setUnits] = useState([]);
    const url = getApiBaseUrl()
    const toast = useToast();

    const [storeSnapshot, setStoreSnapshot] = useState(null);
    const [isLocationCategory, setIsLocationCategory] = useState(false);
    const [locationDetails, setLocationDetails] = useState(() => getDefaultLocationDetails());
    const [locationDocFiles, setLocationDocFiles] = useState({});

    const tokenAmount = getListingTokenAmountFromStore(storeSnapshot);

    const totalPhotos = existingPaths.length + newFiles.length;

    const handleImageUpload = async (event) => {
        const files = Array.from(event.target.files || []);
        event.target.value = "";
        if (!files.length) return;

        const remaining = MAX_PRODUCT_PHOTOS - totalPhotos;
        if (remaining <= 0) {
            toast({
                title: "Maximum photos reached",
                description: `You can have up to ${MAX_PRODUCT_PHOTOS} photos per product.`,
                status: "warning",
                duration: 5000,
                isClosable: true,
                position: "top",
            });
            return;
        }

        const toAdd = [];
        for (const file of files.slice(0, remaining)) {
            const r = await validateImageFileForUpload(file);
            if (!r.ok) {
                toast({
                    title: "Invalid image",
                    description: r.message,
                    status: "error",
                    duration: 6000,
                    isClosable: true,
                    position: "top",
                });
                continue;
            }
            toAdd.push(r.file);
        }
        if (toAdd.length) {
            setNewFiles((prev) => [...prev, ...toAdd].slice(0, MAX_PRODUCT_PHOTOS - existingPaths.length));
        }
    };

    const removeExistingAt = (index) => {
        setExistingPaths((prev) => prev.filter((_, i) => i !== index));
    };

    const removeNewAt = (index) => {
        setNewFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const loadProduct = async (isLoc) => {
        try {
            const response = await fetch(`${url}/product/getsingleproduct/${id}`)
            const res = await response.json();
            setProductName(res.product_name);
            setShiftHour(res.shift_hour || '');
            setSellingPrice(res.sell_price);
            setMrp(res.mrp_price);
            setShortDescription(res.description);
            setExistingPaths(getProductGalleryPaths(res));
            setNewFiles([]);
            setSelectedQuantity(res.quantity);
            setSelectedUnit(res.unit);
            if (isLoc) {
                setLocationDetails(normalizeLocationDetailsFromApi(res.location_details));
            } else {
                setLocationDetails(getDefaultLocationDetails());
            }
            setLocationDocFiles({});
        } catch (error) {
            console.log(error);
        }
    }

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (isLocationCategory) {
            const check = validateLocationDetailsClient(locationDetails, tokenAmount);
            if (!check.ok) {
                toast({
                    title: "Location details",
                    description: check.message,
                    status: "error",
                    duration: 6000,
                    isClosable: true,
                });
                return;
            }
        }

        const formData = new FormData();
        formData.append('existing_product_images', JSON.stringify(existingPaths));
        newFiles.forEach((file) => formData.append('product_images', file));
        formData.append('product_name', productName);
        formData.append('mrp_price', mrp);
        formData.append('sell_price', sellingPrice);
        formData.append('quantity', quantity);
        formData.append('unit', selectedUnit);
        formData.append('description', shortDescription);
        formData.append('user_id', login.permanent_ID);
        formData.append('user_name', login.name);
        formData.append('shift_hour', shiftHour);

        if (isLocationCategory) {
            formData.append("location_details", JSON.stringify(locationDetails));
            formData.append("listing_token_amount", String(tokenAmount));
            appendLocationDocsToFormData(formData, locationDocFiles);
        } else {
            formData.append("clear_location_details", "1");
        }

        try {
            const response = await fetch(`${url}/product/change-product/${id}`, {
                method: 'PUT',
                body: formData,
            });

            const result = await response.json();
            console.log('Image uploaded:', result);
            if (response.ok) {
                navigate(-1)
            } else {
                toast({
                    title: "Update failed",
                    description: result?.error || result?.message || 'Request failed',
                    status: 'error',
                    duration: 6000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.log('Error uploading image:', error);
        }
    };

    const [category, setCategory] = useState();

    const initStoreAndProduct = async () => {
        try {
            const response = await fetch(`${getApiBaseUrl()}/store/getstorelist/mystore/${login.permanent_ID}`);
            const store = await response.json();
            setStoreSnapshot(store);
            setCategory(store.category);
            const isLoc = await resolveIsLocationCategory(url, store.category, store.subcategory);
            setIsLocationCategory(isLoc);
            await loadProduct(isLoc);
        } catch (error) {
            console.log(error);
        }
    }


    const fetchCategory = async () => {
        try {
            const response = await fetch(`${getApiBaseUrl()}/category/singlecategory/${category}`);
            const res = await response.json();
            if (res.units) {
                const parsedUnits = JSON.parse(res.units);
                setUnits(parsedUnits);
            } else {
                console.log('Units field is not available');
            }
        } catch (error) {
            console.log(error);
        }
    };

    const hours = Array.from({ length: 24 }, (_, i) => {
        const value = i + 1;
        return {
            value,
            label: value.toString().padStart(2, '0') + ' hrs'
        };
    });

    useEffect(() => {
        if (category) {
            fetchCategory();
        }
    }, [category]);

    useEffect(() => {
        initStoreAndProduct();
    }, [])

    return (
        <div className="product-container">
            <div className="product-image-column">
                <img
                    src={productadd}
                    alt="Product"
                    className="product-image"
                />
            </div>
            <div className="product-form-column">
                <h2>List Your Product/Skills/Service</h2>
                <form onSubmit={handleSubmit}>
                    <div className="product-form-group">
                        <label>Title:</label>
                        <input
                            type="text"
                            value={productName}
                            onChange={(e) => setProductName(e.target.value)}
                            placeholder="Enter Title"
                            required
                        />
                    </div>
                    <div className="product-form-group">
                        <label>Photos (up to {MAX_PRODUCT_PHOTOS}):</label>
                        <input
                            type="file"
                            accept={IMAGE_ACCEPT_ATTR}
                            multiple
                            onChange={handleImageUpload}
                            disabled={totalPhotos >= MAX_PRODUCT_PHOTOS}
                        />
                        <p className="small text-muted" style={{ marginTop: 6 }}>{IMAGE_TYPES_HELPER_TEXT}</p>
                        <p className="small text-muted" style={{ marginTop: 4 }}>
                            {totalPhotos} / {MAX_PRODUCT_PHOTOS} — first photo is the main thumbnail.
                        </p>
                        {(existingPaths.length > 0 || newFiles.length > 0) && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                                {existingPaths.map((rel, idx) => (
                                    <div key={`ex-${rel}-${idx}`} style={{ position: 'relative', width: 72, height: 72 }}>
                                        <img
                                            src={`${url}/${rel.replace(/\\/g, '/')}`}
                                            alt=""
                                            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => removeExistingAt(idx)}
                                            aria-label="Remove"
                                            style={{
                                                position: 'absolute',
                                                top: -6,
                                                right: -6,
                                                width: 22,
                                                height: 22,
                                                borderRadius: '50%',
                                                border: 'none',
                                                background: 'var(--red, #c53030)',
                                                color: '#fff',
                                                cursor: 'pointer',
                                                fontSize: 12,
                                                lineHeight: 1,
                                            }}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                                {newFiles.map((file, idx) => (
                                    <div key={`nw-${file.name}-${idx}`} style={{ position: 'relative', width: 72, height: 72 }}>
                                        <img
                                            src={URL.createObjectURL(file)}
                                            alt=""
                                            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => removeNewAt(idx)}
                                            aria-label="Remove"
                                            style={{
                                                position: 'absolute',
                                                top: -6,
                                                right: -6,
                                                width: 22,
                                                height: 22,
                                                borderRadius: '50%',
                                                border: 'none',
                                                background: 'var(--red, #c53030)',
                                                color: '#fff',
                                                cursor: 'pointer',
                                                fontSize: 12,
                                                lineHeight: 1,
                                            }}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="product-form-group">
                        <label>Short Description (max 50 chars):</label>
                        <input
                            type="text"
                            value={shortDescription}
                            onChange={(e) => setShortDescription(e.target.value)}
                            maxLength="50"
                            placeholder="Enter short description"
                            required
                        />
                    </div>
                    <div className="product-form-group">
                        <label>Hourly Shift:</label>
                        <select
                            className="bestselecttagforec"
                            value={shiftHour}
                            onChange={(e) => setShiftHour(e.target.value)}
                        >
                            <option value="">Select Hours</option>
                            {hours.map((hour) => (
                                <option key={hour.value} value={hour.value}>
                                    {hour.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="price-container">
                        <div className="product-form-group">
                            <label>Unit:</label>
                            <select
                                className='bestselecttagforec'
                                value={selectedUnit}
                                onChange={(e) => setSelectedUnit(e.target.value)}

                            >
                                <option value="">Select Unit</option>
                                {units.map((unit, index) => (
                                    <option key={index} value={unit}>
                                        {unit}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="product-form-group">
                            <label>Quantity/Value {selectedUnit === "" ? null : ("(" + selectedUnit + ")")}:</label>
                            <input
                                type="quantity"
                                value={quantity}
                                onChange={(e) => setSelectedQuantity(e.target.value)}
                                placeholder={`Enter Value in ${selectedUnit}`}

                            />
                        </div>

                    </div>


                    <div className="price-container">
                        <div className="product-form-group">
                            <label>MRP:</label>
                            <input
                                type="number"
                                value={mrp}
                                onChange={(e) => setMrp(e.target.value)}
                                placeholder="Enter MRP"

                            />
                        </div>
                        <div className="product-form-group">
                            <label>Offer Price:</label>
                            <input
                                type="number"
                                value={sellingPrice}
                                onChange={(e) => setSellingPrice(e.target.value)}
                                placeholder="Enter Offer Price"

                            />
                        </div>
                    </div>
                    {isLocationCategory ? (
                        <LocationProductDetailsForm
                            value={locationDetails}
                            onChange={setLocationDetails}
                            tokenAmount={tokenAmount}
                            onDocFile={(key, file) =>
                                setLocationDocFiles((prev) => {
                                    const next = { ...prev };
                                    if (file) next[key] = file;
                                    else delete next[key];
                                    return next;
                                })
                            }
                        />
                    ) : null}
                    <button type="submit" className="product-submit-button">Edit Product</button>
                </form>
            </div>
        </div>
    );
};

export default EditProduct;
